import React, { memo, FC } from "react";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import { SwingWidthInput, SwingPanelInput } from "./SwingWidthInput";
import Sidelites from "../Sidelites/Sidelites";
import { MenuItem } from "@mui/material";
import ReusableAccordian from "../../ReusableAccordion";
import PleaseNote from "../../PleaseNote";
import { useStore } from "../../../store/store";
import { styled } from "@mui/material/styles";
import { RowLayout, ColLayout } from "../../../util/accordianStyles";
import useWidthSelector from "./useWidthSelector";

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  margin: theme.spacing(1),
  minWidth: 150,
  height: 20,
  position: "relative",
  top: -25,
}));

const StyledTypography = styled(Typography)({
  color: "#3f51b5",
  display: "inline",
  fontWeight: "bold",
});

interface SwingWidthProps {
  widthMeasureImg: string;
  editBtn: boolean | undefined;
  sideLites: string;
  swingType: string;
  direction: string;
  isPivot?: boolean;
}

const SwingWidthAccordian: FC<SwingWidthProps> = ({
  widthMeasureImg,
  editBtn = false,
  sideLites,
  swingType,
  direction,
  isPivot = false,
}) => {
  const {
    handleFractionChange,
    handleWidthChange,
    swingWidths,
    minWidth,
    maxWidth,
    overall,
    overallPW,
    sideliteWidthErr,
    equalPanels,
    unitSize,
    unitSizeFraction,
    swingPanelWidth,
    swingPanelFraction,
  } = useWidthSelector(direction, sideLites, swingType, isPivot, editBtn);

  return (
    <ReusableAccordian
      accordionName="unitSize"
      heading={
        sideLites === "none" || equalPanels ? "Net Frame Width" : "Widths"
      }
      secondaryHeading="Select a Width"
      required5={true}
    >
      <RowLayout>
        {direction && swingType ? (
          <ColLayout>
            <PleaseNote notes={["We recommend Net Frame Width to be 1 inch less than the Rough Opening Width", "For panel widths over 42\", select Pico stiles and rails below, then return here to enter the desired width"]} />

            <RowLayout>
              <SwingWidthInput
                equalPanels={equalPanels || false}
                overall={overall}
                minWidth={minWidth}
                maxWidth={maxWidth}
                inputTitle="Width in Inches"
                width={unitSize}
                handleChange={handleWidthChange}
                option="frame"
              />
              <RowLayout style={{ alignSelf: "center", paddingLeft: 40 }}>
                <StyledFormControl margin="dense" variant="outlined">
                  <InputLabel>fraction</InputLabel>
                  <Select
                    value={unitSizeFraction}
                    onChange={(event) => handleFractionChange(event, "frame")}
                    name="fraction"
                    label="fraction"
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    <MenuItem value={"1/16"}>1/16</MenuItem>
                    <MenuItem value={"1/8"}>1/8</MenuItem>
                    <MenuItem value={"3/16"}>3/16</MenuItem>
                    <MenuItem value={"1/4"}>1/4</MenuItem>
                    <MenuItem value={"5/16"}>5/16</MenuItem>
                    <MenuItem value={"3/8"}>3/8</MenuItem>
                    <MenuItem value={"7/16"}>7/16</MenuItem>
                    <MenuItem value={"1/2"}>1/2</MenuItem>
                    <MenuItem value={"9/16"}>9/16</MenuItem>
                    <MenuItem value={"5/8"}>5/8</MenuItem>
                    <MenuItem value={"11/16"}>11/16</MenuItem>
                    <MenuItem value={"3/4"}>3/4</MenuItem>
                    <MenuItem value={"13/16"}>13/16</MenuItem>
                    <MenuItem value={"7/8"}>7/8</MenuItem>
                    <MenuItem value={"15/16"}>15/16</MenuItem>
                  </Select>
                </StyledFormControl>
              </RowLayout>
            </RowLayout>

            {!sideLites || sideLites === "none" ? (
              <>
                {swingWidths.panelWidth && overall && (
                  <Typography>
                    <StyledTypography>
                      Approximate Per Panel Width(Y):{" "}
                    </StyledTypography>
                    {swingWidths.panelWidth} {swingWidths.panelFraction}"
                  </Typography>
                )}
              </>
            ) : sideLites !== "none" && equalPanels ? (
              <>
                {swingWidths.equalP && (
                  <>
                    <Typography>
                      <StyledTypography>
                        Approximate Per Panel and Per Sidelite Width:{" "}
                      </StyledTypography>
                      {swingWidths.equalP}"
                    </Typography>

                    <Typography style={{ color: "red" }}>
                      {sideliteWidthErr}
                    </Typography>
                  </>
                )}
              </>
            ) : (
              <RowLayout>
                <SwingPanelInput
                  overall={overallPW}
                  inputTitle="Panel Width"
                  panelWidth={swingPanelWidth || ""}
                  handleChange={handleWidthChange}
                  option="panel"
                  isPivot={isPivot || false}
                />
                <RowLayout style={{ alignSelf: "center", paddingLeft: 40 }}>
                  <StyledFormControl margin="dense" variant="outlined">
                    <InputLabel>fraction</InputLabel>
                    <Select
                      value={swingPanelFraction}
                      onChange={(event) => handleFractionChange(event, "panel")}
                      name="fraction"
                      label="fraction"
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      <MenuItem value={"1/16"}>1/16</MenuItem>
                      <MenuItem value={"1/8"}>1/8</MenuItem>
                      <MenuItem value={"3/16"}>3/16</MenuItem>
                      <MenuItem value={"1/4"}>1/4</MenuItem>
                      <MenuItem value={"5/16"}>5/16</MenuItem>
                      <MenuItem value={"3/8"}>3/8</MenuItem>
                      <MenuItem value={"7/16"}>7/16</MenuItem>
                      <MenuItem value={"1/2"}>1/2</MenuItem>
                      <MenuItem value={"9/16"}>9/16</MenuItem>
                      <MenuItem value={"5/8"}>5/8</MenuItem>
                      <MenuItem value={"11/16"}>11/16</MenuItem>
                      <MenuItem value={"3/4"}>3/4</MenuItem>
                      <MenuItem value={"13/16"}>13/16</MenuItem>
                      <MenuItem value={"7/8"}>7/8</MenuItem>
                      <MenuItem value={"15/16"}>15/16</MenuItem>
                    </Select>
                  </StyledFormControl>
                </RowLayout>
              </RowLayout>
            )}

            {!equalPanels && (
              <Sidelites
                swingWidths={swingWidths}
                doorPanelWidth={swingPanelWidth || ""}
                sideLites={sideLites}
              />
            )}
          </ColLayout>
        ) : (
          <PleaseNote notes="Select Swing Configuration to view Width options" />
        )}

        <SwingMeasureImg
          widthMeasureImg={widthMeasureImg}
          equalPanels={equalPanels || false}
        />
      </RowLayout>
    </ReusableAccordian>
  );
};

interface SwingMeasureProps {
  widthMeasureImg: string;
  equalPanels: boolean;
}

const SwingMeasureImg: FC<SwingMeasureProps> = memo(
  ({ widthMeasureImg, equalPanels }) => {
    const exactConfig = useStore((state) => state.quote.exactConfig);

    if (!exactConfig || !widthMeasureImg || equalPanels) return null;

    return (
      <div style={{ paddingLeft: 40 }}>
        <img
          src={widthMeasureImg}
          style={{ width: "auto", height: "auto" }}
          alt=""
        />
      </div>
    );
  }
);

export default memo(SwingWidthAccordian);
